import { useEffect, useMemo } from "react";

import { Box, Flex, HoverCard, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconFileCheck, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button } from "components";
import { ServiceConstants } from "constant";
import { QuickCheckService, UserService } from "services";

import QuickCheckModal from "./QuickCheckModal";
import StatusIndicator from "./StatusIndicator";
import {
  calculateOverallStatus,
  QuickCheckApiResponse,
  QuickCheckData,
  QuickCheckDocument,
  STATUS_COLORS,
  transformApiResponse,
} from "./types";

const { REQUEST_QUICK_CHECK_LIST_ID } = ServiceConstants;

interface QuickCheckButtonProps {
  userId: number;
  blacklisted: boolean;
  isAdmin?: boolean;
}

const QuickCheckButton = ({ userId, blacklisted, isAdmin = true }: QuickCheckButtonProps) => {
  const { t } = useTranslation();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  // Dohvati podatke iz Redux store-a
  const quickCheckListResponse = useSelector(
    (state: any) => state.service.object[REQUEST_QUICK_CHECK_LIST_ID + userId],
  );

  // Transformiši BE response u FE format
  const quickCheckData: QuickCheckData = useMemo(() => {
    const rawData = quickCheckListResponse?.data;
    const apiDocs: QuickCheckApiResponse[] = Array.isArray(rawData) ? rawData : [];
    return transformApiResponse(userId, apiDocs);
  }, [quickCheckListResponse, userId]);

  // Dohvati podatke pri mount-u
  useEffect(() => {
    QuickCheckService.quickCheckList(userId);
  }, [userId]);

  // Refresh podataka nakon zatvaranja modala
  const handleModalClose = () => {
    closeModal();
    QuickCheckService.quickCheckList(userId);
  };

  const overallStatus = calculateOverallStatus(quickCheckData.documents);

  const getDocumentByType = (type: QuickCheckDocument["type"]): QuickCheckDocument | undefined => {
    return quickCheckData.documents.find((doc) => doc.type === type);
  };

  const renderDocumentStatus = (type: QuickCheckDocument["type"]) => {
    const doc = getDocumentByType(type);
    // Dokument postoji ako ima ID (vraćen od BE-a)
    const hasDocument = doc && doc.id;

    return (
      <Flex align="center" justify="space-between" py={8}>
        <Text size="md" c="dark">
          {t(`quickCheck.documents.${type.toLowerCase()}`)}
        </Text>
        {hasDocument ? (
          // Prikaži status ili NONE (sivo) ako status nije definisan
          <StatusIndicator status={doc.status} size={16} />
        ) : (
          <IconX size={18} color="var(--mantine-color-gray-6)" />
        )}
      </Flex>
    );
  };

  return (
    <>
      <HoverCard width={180} shadow="md" position="bottom" withArrow>
        <HoverCard.Target>
          <Button
            variant="transparent"
            size="compact-xs"
            onClick={openModal}
            style={{ position: "relative", overflow: "visible" }}
          >
            <IconFileCheck size={24} color="var(--mantine-color-gray-7)" />
            {/* Overall status indicator dot */}
            <Box
              style={{
                position: "absolute",
                bottom: -1,
                right: 8,
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: STATUS_COLORS[overallStatus || "NONE"],
              }}
            />
          </Button>
        </HoverCard.Target>

        <HoverCard.Dropdown p="md">
          {renderDocumentStatus("BETREIBUNG")}
          {renderDocumentStatus("KRIF")}
          {renderDocumentStatus("ZEK")}
          {blacklisted && (
            <Text c="red" fw={600} mt="sm">
              {t("quickCheck.blacklist")}
            </Text>
          )}
        </HoverCard.Dropdown>
      </HoverCard>

      <QuickCheckModal
        opened={modalOpened}
        onClose={handleModalClose}
        userId={userId}
        blacklisted={blacklisted}
        onBlacklistChanged={() => UserService.userClientsOne(userId)}
        isAdmin={isAdmin}
      />
    </>
  );
};

export default QuickCheckButton;
